import {
  Add,
  Autorenew,
  Campaign,
  Close,
  Extension,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Drawer,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { IndianCurrency } from "app/utils/helper";
import moment from "moment";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import { getProduct } from "app/services/orgService/school.service";

const ADDON_COLORS = {
  SMS: "#1976d2",
  WhatsApp: "#25D366",
  Analytics: "#9c27b0",
  Branding: "#f57c00",
  default: "#546e7a",
};

const getAddonColor = (name = "") => {
  const key = Object.keys(ADDON_COLORS).find((k) =>
    name.toLowerCase().includes(k.toLowerCase()),
  );
  return ADDON_COLORS[key] || ADDON_COLORS.default;
};

const getStatusChipStyle = (status = "") => {
  const normalizedStatus = status.toLowerCase();
  if (normalizedStatus === "paid" || normalizedStatus === "approved") {
    return {
      backgroundColor: "#e8f5e9",
      color: "#2e7d32",
    };
  }
  if (normalizedStatus === "rejected" || normalizedStatus === "cancelled") {
    return {
      backgroundColor: "#fdecea",
      color: "#c62828",
    };
  }
  return {
    backgroundColor: "#fff8e1",
    color: "#ef6c00",
  };
};

const ActiveAddons = ({ session = "", activeAddOnOrders = [] }) => {
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);

  const { data: productsResponse, isLoading: productsLoading } = useQuery({
    queryKey: ["getProduct", session],
    queryFn: () => getProduct({ session }),
    enabled: !!session && marketplaceOpen,
    retry: false,
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to fetch add-ons");
    },
  });

  const addOns = useMemo(
    () =>
      (activeAddOnOrders || []).map((order) => ({
        id: order?._id,
        name:
          order?.productDetail?.productDescription ||
          order?.productDetail?.productName ||
          "Add-on",
        description: order?.productDetail?.productType,
        status: order?.status || "Active",
        activatedOn: order?.dueDate,
        expiresOn: order?.expiresOn,
        amount: order?.netOrderAmount ?? order?.orderAmount ?? 0,
        currency: order?.currency,
      })),
    [activeAddOnOrders],
  );

  const marketplaceAddOns = useMemo(
    () => productsResponse?.data?.list || [],
    [productsResponse?.data?.list],
  );

  return (
    <>
      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardHeader
          title={
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Active Add-ons
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Manage your active subscription add-ons
              </Typography>
            </Box>
          }
          action={
            <Button
              variant="contained"
              size="medium"
              startIcon={<Add />}
              onClick={() => setMarketplaceOpen(true)}
              sx={{ mt: 1, mr: 1 }}
            >
              Add More Add-ons
            </Button>
          }
          sx={{ pb: 1 }}
        />
        <Divider />
        <CardContent>
          {addOns.length === 0 ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              py={4}
              spacing={1}
            >
              <Extension sx={{ fontSize: 48, color: "text.disabled" }} />
              <Typography variant="subtitle1" color="text.secondary">
                No active add-ons for this session
              </Typography>
            </Stack>
          ) : (
            <Grid container spacing={2}>
              {addOns.map((addon, idx) => {
                const color = getAddonColor(addon?.name);
                const status = addon?.status ?? "Active";
                return (
                  <Grid item xs={12} sm={6} md={4} key={addon?.id || idx}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderLeft: `4px solid ${color}`,
                        height: "100%",
                      }}
                    >
                      <CardContent>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          mb={1}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                bgcolor: `${color}20`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Extension sx={{ color, fontSize: 20 }} />
                            </Box>
                            <Typography variant="body1" fontWeight={600}>
                              {addon?.name}
                            </Typography>
                          </Stack>
                          <Chip
                            label={status}
                            size="medium"
                            sx={{
                              ...getStatusChipStyle(status),
                              fontWeight: 500,
                            }}
                          />
                        </Stack>

                        {addon?.description && (
                          <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            mb={1.5}
                          >
                            {addon.description}
                          </Typography>
                        )}

                        <Stack spacing={0.5} mb={2}>
                          {addon?.activatedOn && (
                            <Stack direction="row" spacing={1}>
                              <Typography
                                variant="subtitle1"
                                color="text.secondary"
                              >
                                Activated On
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {moment(addon.activatedOn).format(
                                  "DD MMM YYYY",
                                )}
                              </Typography>
                            </Stack>
                          )}
                          {addon?.expiresOn && (
                            <Stack direction="row" spacing={1}>
                              <Typography
                                variant="subtitle1"
                                color="text.secondary"
                              >
                                Expires On
                              </Typography>
                              <Typography variant="subtitle1" fontWeight={500}>
                                {moment(addon.expiresOn).format("DD MMM YYYY")}
                              </Typography>
                            </Stack>
                          )}
                          <Stack direction="row" spacing={1}>
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                            >
                              Amount
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {addon?.currency || ""}
                              {IndianCurrency(
                                Number(addon?.amount || 0),
                              ).replace(/^[^0-9-]*/, "")}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Button
                          variant="outlined"
                          size="medium"
                          startIcon={<Autorenew />}
                          sx={{ borderRadius: 2, borderColor: color, color }}
                        >
                          Renew
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Drawer
        anchor="right"
        open={marketplaceOpen}
        onClose={() => setMarketplaceOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", md: 720 },
            p: 2,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Add-ons Marketplace
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Enhance your ERP with additional features
            </Typography>
          </Box>
          <IconButton onClick={() => setMarketplaceOpen(false)}>
            <Close />
          </IconButton>
        </Stack>
        <Divider sx={{ my: 2 }} />

        {productsLoading ? (
          <Stack alignItems="center" justifyContent="center" py={8} spacing={1}>
            <Typography variant="subtitle1" color="text.secondary">
              Loading marketplace add-ons...
            </Typography>
          </Stack>
        ) : marketplaceAddOns.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" py={8} spacing={1}>
            <Campaign sx={{ fontSize: 44, color: "text.disabled" }} />
            <Typography variant="subtitle1" color="text.secondary">
              No add-ons available right now
            </Typography>
          </Stack>
        ) : (
          <Grid container spacing={2}>
            {marketplaceAddOns.map((addon, idx) => {
              const marketName =
                addon?.productDescription || addon?.productName || "Add-on";
              const color = getAddonColor(marketName);
              return (
                <Grid item xs={12} md={6} key={addon?._id || idx}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: 2,
                                bgcolor: `${color}20`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Extension sx={{ color, fontSize: 18 }} />
                            </Box>
                            <Typography
                              variant="h5"
                              fontSize="1rem"
                              fontWeight={600}
                            >
                              {marketName}
                            </Typography>
                          </Stack>
                          <Chip label="Available" size="small" />
                        </Stack>

                        <Typography variant="subtitle1" color="text.secondary">
                          Product Code: {addon?.productCode || "-"}
                        </Typography>

                        <Divider />

                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography variant="h6" fontWeight={700}>
                              {addon?.currency || ""}
                              {IndianCurrency(Number(addon?.rate || 0)).replace(
                                /^[^0-9-]*/,
                                "",
                              )}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Qty: {addon?.quantity || 1}
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            size="medium"
                            sx={{ borderRadius: 2 }}
                            onClick={() =>
                              toast.info("Purchase flow will be connected here")
                            }
                          >
                            Purchase
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Drawer>
    </>
  );
};

export default ActiveAddons;
